import { useEffect, useMemo, useState } from 'react'
import Modal from '@components/shared/Modal'

const INITIAL_FORM = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
}

function validatePasswordForm({ currentPassword, newPassword, confirmPassword }, requireCurrentPassword) {
  const errors = {}

  if (requireCurrentPassword && !currentPassword.trim()) {
    errors.currentPassword = 'Informe sua senha atual.'
  }

  if (!newPassword.trim()) {
    errors.newPassword = 'Informe a nova senha.'
  } else if (newPassword.trim().length < 8) {
    errors.newPassword = 'A nova senha deve ter pelo menos 8 caracteres.'
  }

  if (!confirmPassword.trim()) {
    errors.confirmPassword = 'Confirme a nova senha.'
  } else if (confirmPassword !== newPassword) {
    errors.confirmPassword = 'A confirmação deve ser idêntica à nova senha.'
  }

  if (requireCurrentPassword && currentPassword && currentPassword === newPassword) {
    errors.newPassword = 'A nova senha deve ser diferente da senha atual.'
  }

  return errors
}

export default function ChangePasswordModal({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  error = '',
  forced = false,
  requireCurrentPassword = true,
}) {
  const [formValues, setFormValues] = useState(INITIAL_FORM)
  const [formErrors, setFormErrors] = useState({})
  const [showPasswords, setShowPasswords] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setFormValues(INITIAL_FORM)
      setFormErrors({})
      setShowPasswords(false)
    }
  }, [isOpen])

  const subtitle = useMemo(() => (
    forced
      ? 'No primeiro acesso é obrigatório definir uma nova senha antes de continuar.'
      : 'Atualize sua credencial para manter o acesso seguro.'
  ), [forced])

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
    setFormErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const validationErrors = validatePasswordForm(formValues, requireCurrentPassword)
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors)
      return
    }

    const payload = {
      currentPassword: formValues.currentPassword.trim(),
      newPassword: formValues.newPassword.trim(),
      confirmPassword: formValues.confirmPassword.trim(),
    }

    const result = await onSubmit(payload)
    if (result?.success) {
      setFormValues(INITIAL_FORM)
      setFormErrors({})
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={forced ? () => {} : onClose}
      title="Alterar senha"
      subtitle={subtitle}
      icon="fa-key"
      size="small"
      hideCloseButton={forced}
      closeOnOverlayClick={!forced}
    >
      <form className="change-password-form" onSubmit={handleSubmit}>
        {requireCurrentPassword ? (
          <div className="form-group">
            <label htmlFor="currentPassword">Senha atual</label>
            <input
              id="currentPassword"
              type={showPasswords ? 'text' : 'password'}
              className="form-input"
              value={formValues.currentPassword}
              onChange={(event) => handleChange('currentPassword', event.target.value)}
              autoComplete="current-password"
            />
            {formErrors.currentPassword ? <span className="form-helper form-helper-error">{formErrors.currentPassword}</span> : null}
          </div>
        ) : null}

        <div className="form-group">
          <label htmlFor="newPassword">Nova senha</label>
          <input
            id="newPassword"
            type={showPasswords ? 'text' : 'password'}
            className="form-input"
            value={formValues.newPassword}
            onChange={(event) => handleChange('newPassword', event.target.value)}
            autoComplete="new-password"
          />
          {formErrors.newPassword ? <span className="form-helper form-helper-error">{formErrors.newPassword}</span> : null}
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirmar nova senha</label>
          <input
            id="confirmPassword"
            type={showPasswords ? 'text' : 'password'}
            className="form-input"
            value={formValues.confirmPassword}
            onChange={(event) => handleChange('confirmPassword', event.target.value)}
            autoComplete="new-password"
          />
          {formErrors.confirmPassword ? <span className="form-helper form-helper-error">{formErrors.confirmPassword}</span> : null}
        </div>

        <label className="change-password-toggle">
          <input type="checkbox" checked={showPasswords} onChange={() => setShowPasswords((prev) => !prev)} />
          <span>Mostrar senhas digitadas</span>
        </label>

        {error ? <div className="error-message">{error}</div> : null}

        <div className="change-password-actions">
          {!forced ? (
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading}>
              Cancelar
            </button>
          ) : null}
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <><i className="fas fa-spinner fa-spin" /> Salvando...</> : <><i className="fas fa-save" /> Salvar nova senha</>}
          </button>
        </div>
      </form>
    </Modal>
  )
}
